import React, { useState } from 'react';
import { 
  LayoutDashboard, Package, PlusCircle, TrendingDown, RefreshCw,
  ArrowLeftRight, Shield, BarChart3, History, FileText,
  Upload, FileUp, MapPin, FolderTree, Wrench, Trash2, HelpCircle,
  Info, ExternalLink, PlayCircle, AlertCircle
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FeatureModeToggle } from './feature-mode-toggle';
import type { FeatureModule } from '@/types/features';
import { cn } from '@/lib/utils';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Package,
  PlusCircle,
  TrendingDown,
  RefreshCw,
  ArrowLeftRight,
  Shield,
  BarChart3,
  History,
  FileText,
  Upload,
  FileUp,
  MapPin,
  FolderTree,
  Wrench,
  Trash2,
  HelpCircle,
};

interface FeatureModuleCardProps {
  module: FeatureModule;
  onModeChange: (moduleId: string, mode: 'demo' | 'live') => void;
  isLoading?: boolean;
  isRecentlyChanged?: boolean;
}

export const FeatureModuleCard: React.FC<FeatureModuleCardProps> = ({
  module,
  onModeChange,
  isLoading = false,
  isRecentlyChanged = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const IconComponent = iconMap[module.icon] || Package;
  
  const isDisabled = module.status === 'coming-soon' || module.apiRequired;

  const getStatusBadge = () => {
    if (module.status === 'coming-soon') {
      return (
        <Badge variant="warning" accent="warning" className="text-xs">
          Coming Soon
        </Badge>
      );
    }
    
    if (module.apiRequired) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="info" accent="info" className="text-xs cursor-help">
                <AlertCircle className="h-3 w-3 mr-1" />
                API Required
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Live mode requires API connection</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <Badge 
        variant={module.mode === 'live' ? 'success' : 'secondary'} 
        accent={module.mode === 'live' ? 'success' : undefined}
        className="text-xs"
      >
        {module.mode === 'live' ? 'Live' : 'Demo'}
      </Badge>
    );
  };

  return (
    <Card 
      className={cn(
        'transition-all duration-300 hover:shadow-md',
        isRecentlyChanged && 'ring-2 ring-green-500 shadow-lg animate-pulse',
        isDisabled && 'opacity-60'
      )}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          {/* Icon and Info */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className={cn(
              'rounded-lg p-3 flex items-center justify-center shrink-0',
              module.mode === 'live' ? 'bg-green-100' : 'bg-gray-100'
            )}>
              <IconComponent className={cn(
                'h-6 w-6',
                module.mode === 'live' ? 'text-green-600' : 'text-gray-600'
              )} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-base truncate">{module.name}</h3>
                {(module.status === 'coming-soon' || module.apiRequired) && getStatusBadge()}
              </div>
              
              <p className="text-sm text-muted-foreground line-clamp-2">
                {module.description}
              </p>
            </div>
          </div>

          {/* Toggle Switch */}
          <div className="shrink-0">
            <TooltipProvider>
              <Tooltip open={isDisabled && showTooltip}>
                <TooltipTrigger asChild>
                  <div
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <FeatureModeToggle
                      mode={module.mode}
                      disabled={isDisabled}
                      loading={isLoading}
                      onChange={(mode) => onModeChange(module.id, mode)}
                    />
                  </div>
                </TooltipTrigger>
                {isDisabled && (
                  <TooltipContent>
                    <p className="text-xs">
                      {module.status === 'coming-soon' 
                        ? `Coming soon${module.comingSoonDate ? ` - ${module.comingSoonDate}` : ''}`
                        : 'Live mode requires API connection'
                      }
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Usage Stats (if available) */}
        {module.usageStats && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Demo Usage:</span>
                <span className="ml-2 font-medium">{module.usageStats.demoUsage}%</span>
              </div>
              <div>
                <span className="text-muted-foreground">Live Usage:</span>
                <span className="ml-2 font-medium">{module.usageStats.liveUsage}%</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
